<template>
  <div class="kelvin-dropper-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="260" viewBox="0 0 400 260">
          <defs>
             <filter id="glow-spark"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <linearGradient id="metalGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="#94a3b8" />
                <stop offset="100%" stop-color="#475569" />
             </linearGradient>
          </defs>

          <!-- Water Reservoir Tank -->
          <rect x="170" y="10" width="60" height="25" fill="rgba(56, 189, 248, 0.2)" stroke="#38bdf8" rx="2" />
          <path d="M 185,35 L 185,45 M 215,35 L 215,45" stroke="#475569" stroke-width="3" />

          <!-- Induction Rings -->
          <g transform="translate(0, 75)">
             <circle cx="160" cy="0" r="18" fill="none" :stroke="ringLColor" stroke-width="4" filter="url(#glow-spark)" />
             <circle cx="240" cy="0" r="18" fill="none" :stroke="ringRColor" stroke-width="4" filter="url(#glow-spark)" />
             <text x="145" y="-22" :fill="ringLColor" font-size="10" font-weight="900" text-anchor="middle">感应环(+)</text>
             <text x="255" y="-22" :fill="ringRColor" font-size="10" font-weight="900" text-anchor="middle">感应环(-)</text>
          </g>

          <!-- Falling Water Drops with induced charges -->
          <g v-for="d in drops" :key="d.id">
             <circle :cx="d.x" :cy="d.y" :r="2.5" :fill="d.color" />
             <text v-if="Math.abs(charge) > 5" :x="d.x + 8" :y="d.y" :fill="d.color" font-size="8" font-weight="bold">
                {{ d.x < 200 ? '-' : '+' }}
             </text>
          </g>

          <!-- Collection Buckets -->
          <g transform="translate(0, 200)">
             <path d="M 140,0 L 140,40 L 180,40 L 180,0" fill="none" :stroke="ringLColor" stroke-width="3" />
             <path d="M 220,0 L 220,40 L 260,40 L 260,0" fill="none" :stroke="ringRColor" stroke-width="3" />
             <!-- Charge indicators in buckets -->
             <circle v-for="i in Math.min(10, Math.floor(Math.abs(charge)/10))" 
                     :key="'cl'+i" :cx="150 + i*2" cy="20" r="1.5" :fill="ringRColor" />
             <circle v-for="i in Math.min(10, Math.floor(Math.abs(charge)/10))" 
                     :key="'cr'+i" :cx="230 + i*2" cy="20" r="1.5" :fill="ringLColor" />
          </g>

          <!-- Cross-Feedback Wires -->
          <path d="M 160,200 Q 200,160 240,75" fill="none" stroke="rgba(148, 163, 184, 0.4)" stroke-width="2" stroke-dasharray="4,2" />
          <path d="M 240,200 Q 200,160 160,75" fill="none" stroke="rgba(148, 163, 184, 0.4)" stroke-width="2" stroke-dasharray="4,2" />

          <!-- Spark Gap -->
          <g transform="translate(200, 160)">
             <circle cx="-15" cy="0" r="4" fill="url(#metalGrad)" />
             <circle cx="15" cy="0" r="4" fill="url(#metalGrad)" />
             <!-- The Spark! -->
             <path v-if="showSpark" d="M -11,0 L -5,-5 L 5,5 L 11,0" stroke="#fff" stroke-width="2" filter="url(#glow-spark)" />
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>积累电位差 ΔV</label>
              <span class="val">{{ (charge * 200).toFixed(0) }} <small>V</small></span>
           </div>
           <div class="hud-item orange">
              <label>起电效率</label>
              <span class="val">{{ chargeRate.toFixed(2) }} <small>C/s</small></span>
           </div>
           <div class="hud-item red" v-if="showSpark">
              <label>状态</label>
              <span class="val" style="color:#fff;">空气击穿/放电</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>水流流速控制</label>
                 <el-slider v-model="flowRate" :min="1" :max="10" :step="0.5" size="small"></el-slider>
              </div>
              <div class="s-item flex flex-row items-center justify-center">
                 <el-button type="primary" size="small" @click="isRunning = !isRunning" :plain="!isRunning">
                    {{ isRunning ? '切断水源' : '启动水流' }}
                 </el-button>
                 <el-button size="small" @click="resetCharge">人为接地</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>原理详解：</strong>这是一台伟大的“自激”式起电机。它的核心物理机制是<strong>静电感应的正反馈</strong>。初始状态下环境中存在极微小的随机电荷，假设左桶微带正电，通过交叉导线使右环带正电。水滴流经右环时，水滴内部的负电荷受吸引聚集，使其带负电。带负电的水滴落入右桶，又使右桶、左环更负... 如此循环，电压可在数秒内飙升至上万伏，直至产生耀眼的电火花。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KelvinWaterDropperViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      charge: 0.1, // Initial small seed charge
      drops: [],
      isRunning: true,
      flowRate: 5,
      showSpark: false,
      timer: null
    }
  },
  computed: {
    ringLColor() { return this.charge > 0 ? '#ef4444' : '#64748b'; },
    ringRColor() { return this.charge < 0 ? '#38bdf8' : '#64748b'; },
    chargeRate() { return this.flowRate * 0.1; }
  },
  methods: {
    resetCharge() { this.charge = 0.1; this.showSpark = false; },
    animate() {
       if (this.isRunning) {
          // Spawn logic
          if (Math.random() < this.flowRate * 0.1) {
             const side = Math.random() < 0.5 ? 'left' : 'right';
             this.drops.push({
                id: Math.random(),
                x: side === 'left' ? 185 : 215,
                y: 45,
                side: side,
                v: 2 + Math.random(),
                color: '#38bdf8'
             });
          }
       }

       this.showSpark = false;

       for(let i=this.drops.length-1; i>=0; i--) {
          const d = this.drops[i];
          d.y += d.v;
          d.v += 0.2; // gravity

          // Charge colors change as they pass through rings
          if (d.y > 60 && d.y < 90) {
             // Induction happens here
             d.color = d.x < 200 ? (this.charge < 0 ? '#ef4444' : '#38bdf8') : (this.charge > 0 ? '#38bdf8' : '#ef4444');
          }

          if (d.y > 200) {
             // Accumulate based on ring cross-wiring
             // Left nozzle water drop (x~185) should go to left bucket (x~160)
             // In reality they diverge more, here we simplify
             this.charge += (d.x < 200 ? -0.2 : 0.2);
             this.drops.splice(i, 1);
          }
       }

       // Spark logic
       if (Math.abs(this.charge) > 60) {
          if (Math.random() < 0.3) {
             this.showSpark = true;
             this.charge *= 0.5; // partial discharge
          }
       }

       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    if(this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.kelvin-dropper-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } &.red { border-left: 3px solid #ef4444; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr; gap: 15px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.flex { display: flex; } .items-center { align-items: center; } .justify-center { justify-content: center; }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: flex-start; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
